import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from "@/components/ui/toaster"
import { HelmetProvider } from 'react-helmet-async';

import { Button } from "@/components/ui/button"
import { ThemeProvider } from "@/components/theme-provider"
import { AuthProvider } from './context/AuthContext';
import { AiPageContextProvider } from './context/AiPageContextProvider';
import PrivateWrapper from './components/private-wrapper';
import PublicWrapper from './components/public-wrapper';

import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from './components/ErrorFallback';

import Login from './pages/Login';
import VerifyEmail from './pages/VerifyEmail';
import Overview from './pages/Overview';
import NotFound from './pages/NotFound';
import Monitors from './pages/Monitors';
import MonitorDetails from './pages/MonitorDetails';
import CreateMonitor from './pages/CreateMonitor';
import EditMonitor from '@/pages/EditMonitor';
import Projects from './pages/Projects';
import ProjectPage from './pages/ProjectPage';
import TaskDetails from './pages/TaskDetails';
import Onboarding from './pages/Onboarding';
import OrganizationSettings from './pages/OrganizationSettings';
import InvitePage from './pages/InvitePage';
import Preferences from './pages/Preferences';
import Landing from './pages/Landing';
import Changelog from './pages/Changelog';
import Support from './pages/Support';
import TodayPage from './pages/TodayPage';
import ScrollToTop from './components/ScrollToTop';
import Reports from './pages/Reports';
import { SEO } from '@/components/SEO';
import Privacy from './pages/Privacy';
import OrganizationPage from './pages/OrganizationPage';
import CreateOrganization from './pages/CreateOrganization';
import ProjectSettings from './pages/ProjectSettings';
import MagicLinkLanding from './pages/MagicLinkLanding';
import WaitlistSignUpPage from './pages/WaitlistSignUpPage';
import Forms from './pages/Forms';
import FormCreate from './pages/FormCreate';
import FormEdit from './pages/FormEdit';
import FormSubmissions from './pages/FormSubmissions';

import OfflineBanner from './components/OfflineBanner/OfflineBanner';
function App() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <OfflineBanner />
      <HelmetProvider>
        <AuthProvider>
          <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
            <Router>
              <AiPageContextProvider>
                <ScrollToTop />
                <SEO />
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/login" element={
                    <PublicWrapper>
                      <Login />
                    </PublicWrapper>
                  } />
                  <Route path="/verify-email/:tokenId" element={
                    <PublicWrapper>
                      <VerifyEmail />
                    </PublicWrapper>
                  } />
                  <Route path="/magic-link/:tokenId/:tokenValue" element={
                    <PublicWrapper>
                      <MagicLinkLanding />
                    </PublicWrapper>
                  } />
                  <Route path="/waitlist" element={
                    <PublicWrapper>
                      <WaitlistSignUpPage />
                    </PublicWrapper>
                  } />
                  <Route path="/onboarding" element={
                    <PrivateWrapper>
                      <Onboarding />
                    </PrivateWrapper>
                  } />
                  <Route path="/overview" element={
                    <PrivateWrapper>
                      <Overview />
                    </PrivateWrapper>
                  } />
                  <Route path="/today" element={
                    <PrivateWrapper>
                      <TodayPage />
                    </PrivateWrapper>
                  } />
                  <Route path="/monitors" element={
                    <PrivateWrapper>
                      <Monitors />
                    </PrivateWrapper>
                  } />
                  <Route path="/monitors/new" element={
                    <PrivateWrapper>
                      <CreateMonitor />
                    </PrivateWrapper>
                  } />
                  <Route path="/monitors/:id" element={
                    <PrivateWrapper>
                      <MonitorDetails />
                    </PrivateWrapper>
                  }>
                    <Route path="runs/:runId" element={<MonitorDetails />} />
                  </Route>
                  <Route path="/monitors/:id/edit" element={<EditMonitor />} />
                  <Route path="/plan" element={
                    <PrivateWrapper>
                      <Projects />
                    </PrivateWrapper>
                  } />
                  <Route path="/plan/new" element={
                    <PrivateWrapper>
                      <Projects />
                    </PrivateWrapper>
                  } />
                  <Route path="/forms" element={
                    <PrivateWrapper>
                      <Forms />
                    </PrivateWrapper>
                  } />
                  <Route path="/:organizationSlug/forms/create" element={
                    <PrivateWrapper>
                      <FormCreate />
                    </PrivateWrapper>
                  } />
                  <Route path="/:organizationSlug/forms/edit/:formSlug" element={
                    <PrivateWrapper>
                      <FormEdit />
                    </PrivateWrapper>
                  } />
                  <Route path="/:organizationSlug/forms/:formSlug/submissions/:submissionId?" element={
                    <PrivateWrapper>
                      <FormSubmissions />
                    </PrivateWrapper>
                  } />
                  <Route path="/:organizationSlug/plan/:projectKey/:view" element={
                    <PrivateWrapper>
                      <ProjectPage />
                    </PrivateWrapper>
                  } />
                  <Route path="/:organizationSlug/plan/:projectKey/:view/new" element={
                    <PrivateWrapper>
                      <ProjectPage />
                    </PrivateWrapper>
                  } />
                  <Route path="/:organizationSlug/plan/:taskKey" element={
                    <PrivateWrapper>
                      <TaskDetails />
                    </PrivateWrapper>
                  } />
                  <Route path="/:organizationSlug/settings/:tab?" element={
                    <PrivateWrapper>
                      <OrganizationSettings />
                    </PrivateWrapper>
                  } />
                  <Route path="/invite/:token" element={<InvitePage />} />
                  <Route path="/preferences" element={
                    <PrivateWrapper>
                      <Preferences />
                    </PrivateWrapper>
                  } />
                  <Route path="/reports" element={
                    <PrivateWrapper>
                      <Reports />
                    </PrivateWrapper>
                  } />
                  <Route path="/changelog" element={<Changelog />} />
                  <Route path="/support" element={<Support />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/:organizationSlug" element={
                    <PrivateWrapper>
                      <OrganizationPage />
                    </PrivateWrapper>
                  } />
                  <Route path="/organizations/new" element={
                    <PrivateWrapper>
                      <CreateOrganization />
                    </PrivateWrapper>
                  } />
                  <Route path="/:organizationSlug/plan/:projectKey/settings" element={
                    <PrivateWrapper>
                      <ProjectSettings />
                    </PrivateWrapper>
                  } />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </AiPageContextProvider>
            </Router>
            <Toaster />
          </ThemeProvider>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  )
}

export default App
